import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subscription, first } from 'rxjs';
import { ChangePasswordDto } from 'src/app/account/core/models/passwords.model';
import { PasswordComplexitySettings } from 'src/app/admin/core/models/user.model';
import { MismatchValidator } from 'src/app/account/core/validators/mismatch.validator';
import { UsersService } from 'src/app/admin/core/services/users.service';
import { FormErrorStateMatcher } from 'src/app/admin/core/directives/form-error-state-matcher.directive';
import { PasswordStrengthValidator } from 'src/app/admin/core/validators/password-strength.validator';
import { AuthService } from '../core/services/auth.service';
import { ToastService } from 'src/app/_shared/core/services/toast.service';
import { HandleErrorService } from '../core/services/handle-error.service';
import { LoggerCommonService } from 'src/app/_shared/core/services/logger-common.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {
  credentials: ChangePasswordDto;

  passwordForm: UntypedFormGroup;
  passwordFormErrorStateMatcher: FormErrorStateMatcher;

  isLoading = false;
  saving = false;
  subscriptions: Subscription[] = [];

  hideCurrentPassword = true;
  hidePassword = true;
  hideResetPassword = true;

  passwordComplexitySetting: PasswordComplexitySettings = new PasswordComplexitySettings();

  constructor(
    private readonly userService: UsersService, 
    public modal: NgbActiveModal, 
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly handleErrorService: HandleErrorService,
    private readonly logger: LoggerCommonService
  ) { }

  ngOnInit() {
    this.credentials = new ChangePasswordDto();
    this.passwordFormErrorStateMatcher = new FormErrorStateMatcher();

    this.loadPasswordComplexitySetting();
  }

  save() {
    this.saving = true;
    const sb = this.userService.changePassword(this.credentials).pipe(
      finalize(() => {
        this.saving = false;
      })
    ).subscribe({
      next: () => {
        this.modal.close();
      },
      error: (error: any) => {
        this.logger.error(error);
        const message = this.handleErrorService.handleError(error);
        this.toastService.error(message);
        this.saving = false;
        this.passwordForm.get('currentPassword').setValue('');
        this.passwordForm.get('password').setValue('');
        this.passwordForm.get('confirmPassword').setValue('');
      }
    });

    this.subscriptions.push(sb);
  }

  loadPasswordComplexitySetting() {
    this.isLoading = true;
    const sb = this.userService.getPasswordComplexitySetting().pipe(
      first(),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe({
      next: (result) => {
        this.passwordComplexitySetting = result;

        this.buildForm();
        this.formSubscribe();
      },
      error: (error: any) => {
        this.modal.dismiss(error);
        this.logger.error(error);
        throw error;
      }
    });
    this.subscriptions.push(sb);
  }

  buildForm() {
    this.passwordForm = new UntypedFormGroup({
      currentPassword: new UntypedFormControl(''),
      password: new UntypedFormControl(''),
      confirmPassword: new UntypedFormControl('')
    });
    this.passwordForm.get('currentPassword').setValidators(Validators.compose([Validators.required]));
    this.passwordForm.get('password').setValidators(Validators.compose([Validators.required,Validators.maxLength(30), PasswordStrengthValidator.validate(this.passwordComplexitySetting)]));
    this.passwordForm.get('confirmPassword').setValidators(Validators.compose([Validators.required, MismatchValidator.mismatch(this.passwordForm.get('password'))]));
  }

  formSubscribe() {
    this.passwordForm.get('currentPassword').valueChanges.subscribe(value => {
      this.credentials.currentPassword = value;
    });

    this.passwordForm.get('password').valueChanges.subscribe(value => {
      this.credentials.newPassword = value;
      this.passwordForm.get('confirmPassword').updateValueAndValidity();
    });
  }

}
